import { Box, SxProps, Theme, styled } from '@mui/material';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import React from 'react';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import {
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    UseFormStateReturn,
    useFormContext,
} from 'react-hook-form';
import { Button } from '@mui/material'; // Import Button từ Material-UI
import { createStyleSheet } from 'utils/helpers';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
interface BaseImageUploadProps {
    name: string;
    sx?: SxProps;
    multiple?: boolean;
    disabled?: boolean;
}
const getDropzoneColor = (props: DropzoneRootProps, theme: Theme) => {
    if (props.isDragAccept) {
        return theme.palette.success.main;
    }
    if (props.isDragReject) {
        return theme.palette.error.main;
    }
    if (props.isFocused) {
        return theme.palette.primary.main;
    }
    return colors.main;
};
const Dropzone = styled('div')(({ theme, ...props }) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '15rem',
    height: '15rem',
    borderColor: getDropzoneColor(props, theme),
    backgroundColor: common.white,
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
}));
const BaseImageUpload: React.FC<BaseImageUploadProps> = ({
    name,
    multiple,
    disabled,
    sx,
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState, formState }) => (
                <ImageUploadRender
                    field={field}
                    fieldState={fieldState}
                    formState={formState}
                    sx={sx}
                    multiple={multiple}
                    disabled={disabled}
                />
            )}
        />
    );
};

const ImageUploadRender = ({
    field,
    multiple,
    disabled,
    sx,
}: {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
    sx?: SxProps;
    multiple?: boolean;
    disabled?: boolean;
}) => {
    const onDrop = (acceptedFiles: File[]) => {
        if (multiple) {
            field.onChange([...(field.value || []), ...acceptedFiles]);
        } else {
            field.onChange(acceptedFiles[0]);
        }
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        onDrop,
        multiple: multiple,
        disabled: disabled,
    });

    const renderImage = (file: File | string) => {
        if (typeof file === 'string') {
            return (
                <img
                    key={file}
                    src={file}
                    alt={file}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '17px',
                    }}
                />
            );
        } else {
            return (
                <img
                    key={file.name}
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '17px',
                    }}
                />
            );
        }
    };

    return (
        <>
            <Dropzone
                {...getRootProps({
                    isFocused,
                    isDragAccept,
                    isDragReject,
                    sx: {
                        ...sx,
                        position: 'relative',
                        borderRadius: '8px',
                    },
                })}
            >
                <input accept="image/*" {...getInputProps()} />
                {multiple
                    ? field?.value?.map((file: File) => renderImage(file))
                    : field?.value && renderImage(field?.value)}

                <Button
                    variant="outlined"
                    component="span"
                    sx={{
                        ...styles.btnUpload,
                        visibility: disabled ? 'hidden' : 'visible',
                    }}
                    disabled={disabled}
                    startIcon={<FileUploadOutlinedIcon />}
                >
                    Tải ảnh
                </Button>
            </Dropzone>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    fontSize: '20px',
                    whiteSpace: 'normal',
                    marginTop: '28px',
                }}
            >
                <Box
                    sx={{
                        width: '268px',
                        textAlign: 'center',
                        color: 'rgba(0, 0, 0, 0.5)',
                    }}
                >
                    {!disabled
                        ? 'Kéo và thả ảnh tại đây, hoặc nhấp chuột để tải ảnh'
                        : ''}
                </Box>
            </Box>
        </>
    );
};

const styles = createStyleSheet({
    btnUpload: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        backgroundColor: common.white,
        textTransform: 'none',
        minWidth: '170px',
        height: '60px',
        fontSize: '20px',
        fontWeight: '700',
        borderRadius: '40px',
        '&:hover': {
            backgroundColor: common.white,
        },
    },
});

export default BaseImageUpload;
